
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import MessageBox from './MessageBox'
import ModalLayout from '../layouts/ModalLayout'
import SubscriptionInfoModal from './SubscriptionInfoModal'
import { fetchWithAuth } from '../utils/fetchWithAuth'

import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const SubscriptionModal = ({
  open,
  onClose,
  plans,
  userMembershipId,
  membershipValidTo,
  refreshMembership,
  size = 'large' // small / medium / large / fullscreen
}) => {
  const { t } = useTranslation(['translation', 'membership'])
  useAuth()

  const [mode, setMode] = useState('TANKOVANIE')
  const [selected, setSelected] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

  const [paymentPending, setPaymentPending] = useState(false)
  const [hasPendingRenewal, setHasPendingRenewal] = useState(false)
  const [pendingMembershipId, setPendingMembershipId] = useState(null)
  const [pendingActivationAt, setPendingActivationAt] = useState(null)
  const [message, setMessage] = useState(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const [infoPlan, setInfoPlan] = useState(null)
  const [foundingRemaining, setFoundingRemaining] = useState(0)
  const [vatEnabled, setVatEnabled] = useState(false)
  const [availablePlans, setAvailablePlans] = useState([])
  const [availablePlansLoaded, setAvailablePlansLoaded] = useState(false)

  useEffect(() => {
    if (!open) return

    let active = true

    setAvailablePlans([])
    setAvailablePlansLoaded(false)
    setSelected(null)

    fetchWithAuth('/membership/available-types', {
      cacheTtlMs: 0
    })
      .then(res => {
        if (!res.ok) throw new Error('Available memberships unavailable')
        return res.json()
      })
      .then(data => {
        if (active) {
          setAvailablePlans(Array.isArray(data) ? data : [])
          setAvailablePlansLoaded(true)
        }
      })
      .catch(err => {
        console.error('Available memberships load error:', err)

        if (active) {
          setAvailablePlans([])
          setAvailablePlansLoaded(false)
          setMessage({
            type: 'error',
            message: 'Nepodarilo sa načítať dostupné predplatné.'
          })
        }
      })

    return () => {
      active = false
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    let active = true

    fetchWithAuth('/membership/vat-status', {
      cacheTtlMs: 0
    })
      .then(res => {
        if (!res.ok) throw new Error('VAT status unavailable')
        return res.json()
      })
      .then(data => {
        if (active) {
          setVatEnabled(data.vat_enabled === true)
        }
      })
      .catch(() => {
        if (active) setVatEnabled(false)
      })

    return () => {
      active = false
    }
  }, [open])

useEffect(() => {
  if (!open) return

  let active = true

  fetchWithAuth('/auth/me', { cacheTtlMs: 0 })
    .then(res => {
      if (!res.ok) throw new Error('Membership status unavailable')
      return res.json()
    })
    .then(data => {
      if (active) {
        setHasPendingRenewal(
	data.pending_membership_type_id != null
	)
	setPendingMembershipId(data.pending_membership_type_id ?? null)
	setPendingActivationAt(data.pending_activation_at ?? null)
      }
    })
    .catch(console.error)

  return () => {
    active = false
  }
}, [open])

  const RENEWAL_WINDOW_DAYS = 7

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const rawValidTo = membershipValidTo ? new Date(membershipValidTo) : null
  const validTo = rawValidTo ? new Date(rawValidTo) : null
  if (validTo) validTo.setHours(0, 0, 0, 0)

  const isMembershipStillValid = validTo != null && validTo >= today
  const canRenewMembership =
    rawValidTo != null &&
    rawValidTo.getTime() - Date.now() <= RENEWAL_WINDOW_DAYS * 24 * 60 * 60 * 1000

  const formattedValidTo = membershipValidTo
    ? new Date(membershipValidTo).toLocaleDateString('sk-SK')
    : '---'

  const foundingRemainingFormatted =
    new Intl.NumberFormat('sk-SK').format(foundingRemaining)

  useEffect(() => {
    if (!open) return
    if (!Array.isArray(plans) || !plans.length) return
    if (!userMembershipId || !isMembershipStillValid) return

    const activePlan = plans.find(p => Number(p.id) === Number(userMembershipId))
    if (!activePlan) return

    const initialMode = activePlan.is_electric ? 'NABÍJANIE' : 'TANKOVANIE'
    setMode(initialMode)
  }, [open, plans, userMembershipId, isMembershipStillValid])

  useEffect(() => {
    if (!open || isMembershipStillValid) return

    const loadFoundingRemaining = async () => {
      const res = await fetchWithAuth('/public/stats', {
        cacheTtlMs: 60000
      })

      if (!res.ok) return

      const data = await res.json()

	setFoundingRemaining(
	  Math.max(
	    Number(data?.foundingMembershipLimit || 0) -
	      Number(data?.membershipsCount || 0),
	    0
	  )
	)
    }

    loadFoundingRemaining()
  }, [open, isMembershipStillValid])

  const filteredPlans = useMemo(() => {
    const visiblePlans = Array.isArray(availablePlans)
      ? [...availablePlans]
      : []

    if (
      isMembershipStillValid &&
      userMembershipId != null &&
      Array.isArray(plans)
    ) {
      const activePlan = plans.find(
        p => Number(p.id) === Number(userMembershipId)
      )

      if (
        activePlan &&
        !visiblePlans.some(p => Number(p.id) === Number(activePlan.id))
      ) {
        visiblePlans.push(activePlan)
      }
    }

    return visiblePlans
      .filter(p => (mode === 'TANKOVANIE' ? !p.is_electric : p.is_electric))
      .sort((a, b) => Number(a.price) - Number(b.price))
  }, [availablePlans, plans, mode, userMembershipId, isMembershipStillValid])

  const isPlanAvailable = (planId) =>
    availablePlansLoaded &&
    availablePlans.some(p => Number(p.id) === Number(planId))

  const formatPrice = (v) => `€${Math.round(Number(v || 0))}`

  const handleBuy = async (plan) => {
    if (!isPlanAvailable(plan.id)) {
      setSelected(null)
      return
    }

    if (paymentPending) {
      setMessage({ type: 'error', message: 'Platba už prebieha, počkajte na potvrdenie.' })
      return
    }

    setPaymentPending(true)
    setMessage(null)

    try {
      const res = await fetchWithAuth(
        '/membership',
        {
          method: 'POST',
          body: JSON.stringify({ membership_type_id: plan.id })
        }
      )

      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        setMessage({ type: 'error', message: data.message || 'Error' })
        return
      }

      const successUrl = `${window.location.origin}/home?status=success`
      const cancelUrl = `${window.location.origin}/home?status=cancel`

      const stripeRes = await fetchWithAuth(
        '/pay/init',
        {
          method: 'POST',
          body: JSON.stringify({
            amount: data.price,
            membership_type_id: plan.id,
	    membership_label: t('MEMBERSHIP'),
            membership_type: data.membership_type,
            duration_months: data.duration_months,
            success_url: successUrl,
            cancel_url: cancelUrl
          })
        }
      )

      const stripeData = await stripeRes.json().catch(() => ({}))

      if (!stripeData.url) {
        setMessage({ type: 'error', message: 'Chyba pri vytváraní platby' })
        return
      }

      sessionStorage.setItem('stripe_checkout_started_at', String(Date.now()))
      window.location.href = stripeData.url
    } catch (e) {
      console.error(e)
      setMessage({ type: 'error', message: 'Chyba pripojenia' })
    } finally {
      setPaymentPending(false)
      try {
        if (typeof refreshMembership === 'function') await refreshMembership()
      } catch {}
    }
  }

  if (!open) return null

  return (
    <ModalLayout
      open={open}
      onClose={() => {
        if (!infoOpen) onClose()
      }}
      size={size}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 20,
          marginBottom: 15
        }}
      >
        <h3 style={{ fontSize: 24, margin: 0 }}>
          {t('HOME_SUBSCRIPTION')}
        </h3>

        {!isMembershipStillValid && (
          <div
            style={{
              fontSize: 15,
              textAlign: 'right'
            }}
            dangerouslySetInnerHTML={{
              __html: t('FOUNDING_REMAINING', {
                count: foundingRemainingFormatted
              })
            }}
          />
        )}
      </div>

      {message && <MessageBox message={message.message} type={message.type} />}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <button
          style={{
            ...modalSecondaryButtonStyle(pressedBtn, 'fuel', hoverBtn === 'fuel'),
            background: mode === 'TANKOVANIE' ? '#1e3f44' : '#e0e0e0',
            color: mode === 'TANKOVANIE' ? '#fff' : '#000',
            fontWeight: mode === 'TANKOVANIE' ? 900 : 600
          }}
          onMouseEnter={() => setHoverBtn('fuel')}
          onMouseLeave={() => setHoverBtn(null)}
          onMouseDown={() => setPressedBtn('fuel')}
          onMouseUp={() => setPressedBtn(null)}
          onClick={() => setMode('TANKOVANIE')}
        >
          {t('MODE_FUEL')}
        </button>

        <button
          style={{
            ...modalSecondaryButtonStyle(pressedBtn, 'charge', hoverBtn === 'charge'),
            background: mode === 'NABÍJANIE' ? '#1e3f44' : '#e0e0e0',
            color: mode === 'NABÍJANIE' ? '#fff' : '#000',
            fontWeight: mode === 'NABÍJANIE' ? 900 : 600
          }}
          onMouseEnter={() => setHoverBtn('charge')}
          onMouseLeave={() => setHoverBtn(null)}
          onMouseDown={() => setPressedBtn('charge')}
          onMouseUp={() => setPressedBtn(null)}
          onClick={() => setMode('NABÍJANIE')}
        >
          {t('MODE_CHARGE')}
        </button>
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {filteredPlans.map((p) => {
	const isActive = Number(userMembershipId) === Number(p.id) && isMembershipStillValid
	const isNextMembership =
	  hasPendingRenewal &&
	  pendingMembershipId != null &&
	  Number(pendingMembershipId) === Number(p.id)
	const isAvailable = isPlanAvailable(p.id)
	const disabledChoose =
	  !isAvailable ||
	  hasPendingRenewal ||
	  (isMembershipStillValid && !canRenewMembership && !isActive)
          const isPaymentLocked = paymentPending && !isActive

          return (
            <div
              key={p.id}
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: 16,
                marginBottom: 12,
                border: isActive ? '2px solid #1e3f44' : '1px solid #ccc'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>
                    {t(p.type, { ns: 'membership', defaultValue: p.type })}

                    {isActive ? (
                      <span
                        style={{
                          fontSize: 16,
                          marginLeft: 10,
                          fontWeight: 900,
                          color: 'red'
                        }}
                      >
                        {t('ACTIVE_BADGE')}

                        {membershipValidTo && canRenewMembership ? (
                          <span
                            style={{
                              marginLeft: 6,
                              fontWeight: 900,
                              fontSize: 14,
                              color: '#000'
                            }}
                          >
                            ({t('VALID_TO')}{' '}
                            <span style={{ color: 'red', fontWeight: 900 }}>
                              {formattedValidTo}
                            </span>
                            )
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </div>

                  <div style={{ marginTop: 6, opacity: 0.85 }}>
                    {t('DISCOUNT_PER_UNIT', {
                      value: p.discount_per_unit,
                      unit: p.is_electric ? t('UNIT_EUR_PER_KWH') : t('UNIT_EUR_PER_L')
                    })}
                  </div>

                  <div style={{ marginTop: 6, opacity: 0.85 }}>
                    {p.cashback_limit
                      ? t('CASHBACK_LIMIT', { value: p.cashback_limit })
                      : t('CASHBACK_NO_LIMIT')}
                  </div>

                  <div style={{ marginTop: 6, opacity: 0.85 }}>
                    {t('MAX_CASHBACK', { value: p.max_cashback })}
                  </div>

                  {p.max_amount != null ? (
                    <div style={{ marginTop: 6, opacity: 0.85 }}>
                      {t('MAX_AMOUNT', {
                        value: p.max_amount,
                        unit: p.is_electric ? 'kWh' : 'l'
                      })}
                    </div>
                  ) : null}
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, opacity: 0.8 }}>
                    {t('PRICE_LABEL')}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 900 }}>
                    {formatPrice(p.price)}
                  </div>
                  {vatEnabled && (
                    <div style={{ fontSize: 13, opacity: 0.8 }}>
                      {t('PRICE_INCLUDING_VAT')}
                    </div>
                  )}
                  <div style={{ fontSize: 14, fontWeight: 700, opacity: 0.8 }}>
                    {t('PER_MONTH_SHORT', { count: p.duration_months })}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                <button
                  style={modalSecondaryButtonStyle(
                    pressedBtn,
                    `detail-${p.id}`,
                    hoverBtn === `detail-${p.id}`
                  )}
                  onMouseEnter={() => setHoverBtn(`detail-${p.id}`)}
                  onMouseLeave={() => {
                    setHoverBtn(null)
                    setPressedBtn(null)
                  }}
                  onMouseDown={() => setPressedBtn(`detail-${p.id}`)}
                  onMouseUp={() => setPressedBtn(null)}
                  onClick={(e) => {
                    e.currentTarget.blur()
                    setInfoPlan(p)
                    setInfoOpen(true)
                  }}
                >
                  {t('DETAILS')}
                </button>

                {(isActive && !hasPendingRenewal && (!canRenewMembership || !isAvailable)) || isNextMembership ? (
                  <button
                    style={{
                      ...modalPrimaryButtonStyle(pressedBtn, `active-${p.id}`, false),
                      cursor: 'default'
                    }}
                    disabled
                  >
                    {isNextMembership
		  ? `${t('VALID_FROM')}: ${pendingActivationAt
 		     ? new Date(pendingActivationAt).toLocaleDateString('sk-SK')
 		     : '---'}`
 		 : `${t('VALID_TO')}: ${formattedValidTo}`}
                  </button>
                ) : (
                  <button
                    style={{
                      ...modalPrimaryButtonStyle(
                        pressedBtn,
                        `choose-${p.id}`,
                        selected === p.id ? false : hoverBtn === `choose-${p.id}`
                      ),
                      padding: '10px 35px',
                      ...(selected === p.id && {
                        backgroundColor: '#d32f2f',
                        borderColor: '#b71c1c',
                        color: '#fff'
                      }),
                      opacity: (disabledChoose || isPaymentLocked) ? 0.5 : 1,
                      cursor: (disabledChoose || isPaymentLocked) ? 'not-allowed' : 'pointer'
                    }}
                    disabled={disabledChoose || isPaymentLocked}
                    onMouseEnter={() => setHoverBtn(`choose-${p.id}`)}
                    onMouseLeave={() => {
                      setHoverBtn(null)
                      setPressedBtn(null)
                    }}
                    onMouseDown={() => setPressedBtn(`choose-${p.id}`)}
                    onMouseUp={() => setPressedBtn(null)}
                    onClick={() => {
                      if (disabledChoose || isPaymentLocked) return
                      if (selected !== p.id) setSelected(p.id)
                      else handleBuy(p)
                    }}
                  >
                    {selected === p.id ? t('BUY') : t('CHOOSE')}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div
        style={{
          borderTop: '1px solid #ddd',
          paddingTop: 14,
          paddingBottom: 0,
          display: 'flex',
          justifyContent: 'center',
          marginTop: 16
        }}
      >
        <button
          style={modalSecondaryButtonStyle(pressedBtn, 'close', hoverBtn === 'close')}
          onMouseEnter={() => setHoverBtn('close')}
          onMouseLeave={() => {
            setHoverBtn(null)
            setPressedBtn(null)
          }}
          onMouseDown={() => setPressedBtn('close')}
          onMouseUp={() => setPressedBtn(null)}
          onClick={onClose}
        >
          {t('CLOSE')}
        </button>
      </div>

      <SubscriptionInfoModal
        visible={infoOpen}
        plan={infoPlan}
        onClose={() => setInfoOpen(false)}
        userMembershipId={userMembershipId}
        membershipValidTo={membershipValidTo}
      />
    </ModalLayout>
  )
}

export default SubscriptionModal
